
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ActivityIcon } from 'lucide-react';
import DeviceHeader from './DeviceHeader';
import DeviceNotFound from './DeviceNotFound';
import WaterMeterMetrics from './WaterMeterMetrics';
import GatewayMetrics from './GatewayMetrics';
import NetworkInformation from './NetworkInformation';
import MaintenanceInformation from './MaintenanceInformation';
import WaterMeterReadings from './WaterMeterReadings';
import GatewayPerformance from './GatewayPerformance';
import { getStatusIcon } from '../device-health/SmartMeterStatusHelpers';
import { Device } from '@/components/device-health/gis-map/types';

interface WaterDeviceDetailViewProps {
  deviceId: string;
  deviceData: any;
  gisDeviceData?: Device | null;
}

const WaterDeviceDetailView: React.FC<WaterDeviceDetailViewProps> = ({ deviceId, deviceData, gisDeviceData }) => {
  // Determine if the device is a smart meter or gateway
  const isSmartMeter = deviceData?.smartMeters.some((meter: any) => meter.deviceId === deviceId);
  const isGateway = deviceData?.gateways.some((gateway: any) => gateway.deviceId === deviceId);
  
  // Get the device data
  const device = isSmartMeter 
    ? deviceData?.smartMeters.find((meter: any) => meter.deviceId === deviceId)
    : deviceData?.gateways.find((gateway: any) => gateway.deviceId === deviceId);

  if (!device && !gisDeviceData) {
    return <DeviceNotFound />;
  }

  // If we have GIS device data but no device record, create a minimal device object
  const deviceToUse = device || {
    deviceId: gisDeviceData?.id || deviceId,
    location: gisDeviceData?.location?.address || 'Unknown',
    status: gisDeviceData?.status || 'Unknown',
    lastCommunication: gisDeviceData?.lastActive || new Date().toISOString(),
  };

  return (
    <Card>
      <DeviceHeader 
        device={deviceToUse} 
        isSmartMeter={isSmartMeter || (gisDeviceData?.type === 'smartMeter')} 
        deviceType="Smart Water Meter"
        gisDeviceData={gisDeviceData}
      />

      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          {isSmartMeter || (gisDeviceData?.type === 'smartMeter') ? (
            <WaterMeterMetrics device={deviceToUse} />
          ) : (
            <GatewayMetrics device={deviceToUse} />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <NetworkInformation device={deviceToUse} isSmartMeter={isSmartMeter || (gisDeviceData?.type === 'smartMeter')} />
          <MaintenanceInformation device={deviceToUse} />
        </div>

        {/* Additional device-specific information */}
        {(isSmartMeter || (gisDeviceData?.type === 'smartMeter')) && <WaterMeterReadings device={deviceToUse} />}
        {(isGateway || (gisDeviceData?.type === 'gateway')) && <GatewayPerformance device={deviceToUse} />}
      </CardContent>
    </Card>
  );
};

export default WaterDeviceDetailView;
